/**
 * Inline outline icons (replacing the Material Symbols webfont from the design
 * mockup) so there are no external font requests. Stroke uses currentColor.
 */
export type IconName =
  | 'menu'
  | 'search'
  | 'moon'
  | 'sun'
  | 'bell'
  | 'calendar'
  | 'grid'
  | 'groups'
  | 'badge'
  | 'monitoring'
  | 'description'
  | 'settings'
  | 'person'
  | 'login'
  | 'timer'
  | 'trending_up'
  | 'chevron_right'
  | 'arrow_forward'
  | 'premium'
  | 'download'
  | 'manage_accounts'
  | 'apartment'
  | 'summarize'
  | 'grip'
  | 'eye'
  | 'eye_off'
  | 'edit'
  | 'target'
  | 'plus'
  | 'comment'
  | 'trash'
  | 'check'
  | 'x'
  | 'image'
  | 'upload'
  | 'bulb'

export function Icon({ name, className = 'w-5 h-5' }: { name: IconName; className?: string }) {
  const p = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (name) {
    case 'menu':
      return <svg {...p}><path d="M4 6h16M4 12h16M4 18h16" /></svg>
    case 'search':
      return <svg {...p}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
    case 'moon':
      return <svg {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></svg>
    case 'sun':
      return <svg {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
    case 'bell':
      return <svg {...p}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>
    case 'calendar':
      return <svg {...p}><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>
    case 'grid':
      return <svg {...p}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>
    case 'groups':
      return <svg {...p}><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /><circle cx="9" cy="7" r="3.2" /><path d="M16 3.5a3.2 3.2 0 0 1 0 6.4M21 21v-2a4 4 0 0 0-3-3.8" /></svg>
    case 'badge':
      return <svg {...p}><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M9 3h6v3H9zM8 16a3 3 0 0 1 6 0" /><circle cx="11" cy="12" r="1.6" /></svg>
    case 'monitoring':
      return <svg {...p}><path d="M3 12h4l2.5 7 5-15L17 12h4" /></svg>
    case 'description':
      return <svg {...p}><path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>
    case 'settings':
      return <svg {...p}><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.1l2-1.5-2-3.4-2.3.9a7 7 0 0 0-1.9-1.1L14.3 2h-4l-.4 2.7a7 7 0 0 0-1.9 1.1l-2.3-.9-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .7.1 1.1l-2 1.5 2 3.4 2.3-.9c.6.5 1.2.8 1.9 1.1l.4 2.7h4l.4-2.7a7 7 0 0 0 1.9-1.1l2.3.9 2-3.4-2-1.5c.1-.4.1-.7.1-1.1Z" /></svg>
    case 'person':
      return <svg {...p}><circle cx="12" cy="8" r="3.4" /><path d="M5 20v-1.5A5.5 5.5 0 0 1 10.5 13h3A5.5 5.5 0 0 1 19 18.5V20" /></svg>
    case 'login':
      return <svg {...p}><path d="M15 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3" /></svg>
    case 'timer':
      return <svg {...p}><circle cx="12" cy="13" r="8" /><path d="M12 13V9M9 2h6" /></svg>
    case 'trending_up':
      return <svg {...p}><path d="M3 17l6-6 4 4 8-8M15 7h6v6" /></svg>
    case 'chevron_right':
      return <svg {...p}><path d="m9 6 6 6-6 6" /></svg>
    case 'arrow_forward':
      return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
    case 'premium':
      return <svg {...p}><circle cx="12" cy="9" r="6" /><path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" /></svg>
    case 'bulb':
      return <svg {...p}><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.5h6c0-1.1.4-1.9 1-2.5A6 6 0 0 0 12 3Z" /></svg>
    case 'download':
      return <svg {...p}><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
    case 'manage_accounts':
      return <svg {...p}><circle cx="10" cy="8" r="3.2" /><path d="M4 20v-1.5A5 5 0 0 1 9 13.5h1.5" /><circle cx="18" cy="17" r="1.6" /><path d="M18 14v1M18 19v1M20.6 15.5l-.9.5M16.3 18l-.9.5M20.6 18.5l-.9-.5M16.3 15l-.9-.5" /></svg>
    case 'apartment':
      return <svg {...p}><path d="M4 21V6l7-3v18M11 21V9l7 3v9M4 21h16M7 9h1M7 12h1M7 15h1M14 15h1M14 18h1" /></svg>
    case 'summarize':
      return <svg {...p}><path d="M7 3h10a1 1 0 0 1 1 1v17l-3-2-3 2-3-2-3 2V4a1 1 0 0 1 1-1Z" /><path d="M9 8h6M9 12h6" /></svg>
    case 'grip':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <circle cx="9" cy="6" r="1.4" /><circle cx="15" cy="6" r="1.4" />
          <circle cx="9" cy="12" r="1.4" /><circle cx="15" cy="12" r="1.4" />
          <circle cx="9" cy="18" r="1.4" /><circle cx="15" cy="18" r="1.4" />
        </svg>
      )
    case 'eye':
      return <svg {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
    case 'eye_off':
      return <svg {...p}><path d="M3 3l18 18M10.6 5.1A9 9 0 0 1 21 12a9.7 9.7 0 0 1-2.3 3M6.1 6.1A9.7 9.7 0 0 0 3 12a9 9 0 0 0 12 8.5" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>
    case 'edit':
      return <svg {...p}><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
    case 'target':
      return <svg {...p}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" fill="currentColor" /></svg>
    case 'plus':
      return <svg {...p}><path d="M12 5v14M5 12h14" /></svg>
    case 'comment':
      return <svg {...p}><path d="M21 12a8 8 0 0 1-11.6 7.1L3 20l1-5.3A8 8 0 1 1 21 12Z" /></svg>
    case 'trash':
      return <svg {...p}><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" /></svg>
    case 'check':
      return <svg {...p}><path d="M5 12.5 10 17.5 20 6.5" /></svg>
    case 'x':
      return <svg {...p}><path d="M6 6l12 12M18 6 6 18" /></svg>
    case 'image':
      return <svg {...p}><rect x="3" y="4.5" width="18" height="15" rx="2" /><circle cx="8.5" cy="9.5" r="1.6" /><path d="m4 17 5-5 4 4 3-3 4 4" /></svg>
    case 'upload':
      return <svg {...p}><path d="M12 16V4M7 9l5-5 5 5M5 20h14" /></svg>
    default:
      return <svg {...p}><circle cx="12" cy="12" r="8" /></svg>
  }
}

/** Nav item id → icon name. */
export const NAV_ICON: Record<string, IconName> = {
  dashboard: 'grid',
  customers: 'groups',
  users: 'badge',
  companies: 'apartment',
  adminSettings: 'manage_accounts',
  mailpit: 'comment',
  activity: 'monitoring',
  zeroCollections: 'summarize',
  unknownMaterials: 'search',
  verkefni: 'target',
  settings: 'settings',
}
